<script lang="ts">
import type { RadioGroupItem, RadioGroupProps } from '@nuxt/ui';
import type { SelectFieldOptions } from '#layers/data-model';
import type { InputBlockProps } from '~/types/input-block';

interface InputRadioBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<RadioGroupProps, 'size' | 'variant' | 'color'> {
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_radio.name',
	description: '$t:blocks.input_radio.description',
	icon: 'lucide:circle-check',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ({ typeOptions }) => !typeOptions?.multiple ? ['select'] : [] },
	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'options',
			block: BuiltinBlockIds.InputRepeater,
			props: {
				schema: [
					{
						name: 'value',
						label: '$t:common.value',
						block: BuiltinBlockIds.Input,
					},
					{
						name: 'label',
						label: '$t:common.label',
						block: BuiltinBlockIds.Input,
					},
					{
						name: 'icon',
						label: '$t:common.icon',
						block: BuiltinBlockIds.InputIcon,
					},
					{
						name: 'color',
						label: '$t:common.color',
						block: BuiltinBlockIds.InputColor,
					},
				],
				displayTemplate: '{{label}}',
			},
		},
		{
			name: 'multiple',
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'disabled',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'readonly',
		},
	],
}</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';
import { DRadioGroup } from '#components';
import { defineBlock, useBlockInEditorContext } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { useDisabledNotice, useReadonly } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';
import { useSelectOptions } from '~/composables/use-select-options';

const props = defineProps<InputRadioBlockProps>();
const model = defineModel<any>();

;

const forwarded = reactivePick(props, 'size', 'variant', 'color');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { normalizedOptions: items } = useSelectOptions<RadioGroupItem>(() => props.options);

const { isPreview } = useBlockInEditorContext();
const previewItems = computed<RadioGroupItem[]>(() => Array.from({ length: 3 }, (_, i) => ({
	value: `option-${i + 1}`,
	label: `Option ${i + 1}`,
}) satisfies RadioGroupItem));
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DRadioGroup
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:aria-readonly="readonly"
			:items="isPreview ? previewItems : items"
			:ui="{
				item: props.readonly ? 'pointer-events-none' : '',
			}"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
	</DTooltip>
</template>
